import { useState } from "react";
import axios from "axios";
import {
    Star,
    Loader2,
    CheckCircle2,
    MessageSquare,
    Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { track } from "@/lib/track";
import Breadcrumb from "@/components/Breadcrumb";
import { EditableText } from "@/lib/editMode";

const HEARD_OPTIONS = [
    "Google search",
    "Instagram",
    "Facebook",
    "Friend / referral",
    "Word of mouth",
    "Local car group / forum",
    "Other",
];

const initial = {
    name: "",
    email: "",
    phone: "",
    heard_from: "",
    heard_from_other: "",
    rating: 0,
    feedback_text: "",
    can_share: false,
};

export default function Feedback() {
    useSEO({
        title: "Leave Feedback — Help Limit Break Get Better",
        description:
            "Share your experience with Limit Break Performance. Anonymous OK. Your honest feedback helps me improve and lets future customers know what to expect.",
    });

    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const setRating = (r) => setForm((f) => ({ ...f, rating: r }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.feedback_text.trim()) {
            toast.error("Drop a note in the feedback box first.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                rating: form.rating || null,
                name: form.name || null,
                email: form.email || null,
                phone: form.phone || null,
                heard_from: form.heard_from || null,
                heard_from_other:
                    form.heard_from === "Other" ? form.heard_from_other : null,
            };
            await axios.post(`${API}/feedback`, payload);
            setSubmitted(true);
            setForm(initial);
            toast.success("Thanks — feedback received.");
            track("feedback_submit", {
                rating: form.rating || 0,
                shared_publicly: !!form.can_share,
                heard_from: form.heard_from || "unknown",
            });
        } catch {
            toast.error("Couldn't send. Try again or text us.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="feedback-page">
            <Breadcrumb section="Feedback" />

            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-8 pb-14 sm:pb-20 lg:pb-24">
                    <EditableText
                        path="feedback.page.eyebrow"
                        defaultValue="Tell me how I'm doing"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="feedback.page.title"
                        defaultValue="Feedback"
                        as="h1"
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                        data-testid="feedback-hero-title"
                    />
                    <EditableText
                        path="feedback.page.subtitle"
                        defaultValue="Honest feedback only. Whether the job went great or something missed the mark — I want to hear it. Anonymous is fine. Your responses go straight to me."
                        multiline
                        as="p"
                        className="mt-4 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                    />
                    <p className="mt-3 inline-flex items-center gap-2 text-xs text-zinc-500">
                        <Lock className="w-3.5 h-3.5" /> <EditableText path="feedback.page.privacy_note" defaultValue="Private by default. Nothing gets posted unless you check the share box." as="span" />
                    </p>
                </div>
            </section>

            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-12 sm:py-16 max-w-2xl">
                    {submitted ? (
                        <div
                            data-testid="feedback-success-state"
                            className="rounded-sm border border-green-500/30 bg-green-500/5 p-8 text-center"
                        >
                            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
                            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl">
                                Thanks For The Feedback
                            </h2>
                            <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                                Means a lot. I read every single one. If you
                                left contact info I might reach back out — if
                                not, you're done. 🙏
                            </p>
                            <button
                                type="button"
                                data-testid="feedback-send-another"
                                onClick={() => setSubmitted(false)}
                                className="mt-6 inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-6 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                Send Another
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="feedback-form"
                            noValidate
                            className="rounded-sm border border-white/10 bg-zinc-900 p-6 sm:p-8 space-y-5"
                        >
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                    Where did you hear about us?
                                </Label>
                                <select
                                    name="heard_from"
                                    value={form.heard_from}
                                    onChange={onChange}
                                    data-testid="feedback-input-heard-from"
                                    className="mt-2 w-full h-11 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-white focus:outline-none focus:ring-2 focus:ring-red-600"
                                >
                                    <option value="">Pick one…</option>
                                    {HEARD_OPTIONS.map((o) => (
                                        <option key={o} value={o}>
                                            {o}
                                        </option>
                                    ))}
                                </select>
                                {form.heard_from === "Other" && (
                                    <Input
                                        name="heard_from_other"
                                        value={form.heard_from_other}
                                        onChange={onChange}
                                        data-testid="feedback-input-heard-from-other"
                                        placeholder="Where exactly?"
                                        className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                    />
                                )}
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                    Rating (optional)
                                </Label>
                                <div className="mt-2 flex items-center gap-1.5" data-testid="feedback-rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setRating(n === form.rating ? 0 : n)}
                                            data-testid={`feedback-rating-${n}`}
                                            aria-label={`Rate ${n} of 5`}
                                            className="p-1 rounded-sm hover:bg-white/5"
                                        >
                                            <Star
                                                className={`w-7 h-7 transition-colors ${
                                                    n <= form.rating
                                                        ? "text-amber-400 fill-amber-400"
                                                        : "text-zinc-600"
                                                }`}
                                            />
                                        </button>
                                    ))}
                                    {form.rating ? (
                                        <span className="ml-2 text-sm text-zinc-400">
                                            {form.rating}/5
                                        </span>
                                    ) : null}
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                    Your Feedback *
                                </Label>
                                <Textarea
                                    name="feedback_text"
                                    value={form.feedback_text}
                                    onChange={onChange}
                                    data-testid="feedback-input-text"
                                    placeholder="Tell me how it went — the good, the bad, what to keep doing, what to fix."
                                    className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 min-h-[140px]"
                                />
                            </div>

                            <div className="border-t border-white/5 pt-5 space-y-4">
                                <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">
                                    Optional — leave blank to stay anonymous
                                </p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                            Name
                                        </Label>
                                        <Input
                                            name="name"
                                            value={form.name}
                                            onChange={onChange}
                                            data-testid="feedback-input-name"
                                            placeholder="Your name"
                                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                            Phone
                                        </Label>
                                        <Input
                                            name="phone"
                                            value={form.phone}
                                            onChange={onChange}
                                            data-testid="feedback-input-phone"
                                            placeholder="(757) 555-0123"
                                            type="tel"
                                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                        Email
                                    </Label>
                                    <Input
                                        name="email"
                                        value={form.email}
                                        onChange={onChange}
                                        data-testid="feedback-input-email"
                                        placeholder="you@email.com"
                                        type="email"
                                        className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                    />
                                </div>
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer rounded-sm border border-white/10 bg-zinc-950 p-4 hover:border-red-600/40 transition-colors">
                                <input
                                    type="checkbox"
                                    name="can_share"
                                    checked={form.can_share}
                                    onChange={onChange}
                                    data-testid="feedback-input-can-share"
                                    className="mt-1 w-4 h-4 accent-red-600"
                                />
                                <span className="text-sm text-zinc-300 leading-relaxed">
                                    OK to share this on the site or my Google
                                    page (with name only — never your phone or
                                    email).
                                    <span className="block text-xs text-zinc-500 mt-1">
                                        If unchecked, your feedback stays private — only I see it.
                                    </span>
                                </span>
                            </label>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="feedback-submit-button"
                                className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white px-6 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        <MessageSquare className="w-4 h-4" />
                                        Send Feedback
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
